import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, AddOnCategory, AddOnOption, MenuItem } from '../../services/dataService';

interface AddOnsSelectionModalProps {
  menuItem: MenuItem;
  onClose: () => void;
  onConfirm: (selectedAddOns: Array<{
    categoryId: string;
    categoryName: string;
    selectedOptions: Array<{
      optionId: string;
      optionName: string;
      price: number;
    }>;
  }>) => void;
}

const AddOnsSelectionModal: React.FC<AddOnsSelectionModalProps> = ({ 
  menuItem, 
  onClose, 
  onConfirm 
}) => {
  const { tenantData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [addOnCategories, setAddOnCategories] = useState<AddOnCategory[]>([]);
  const [addOnOptions, setAddOnOptions] = useState<AddOnOption[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<Map<string, Set<string>>>(new Map());
  const [error, setError] = useState('');

  // Load add-ons configuration for this menu item
  useEffect(() => {
    const loadAddOns = async () => {
      if (!tenantData?.id) return;
      
      try {
        setLoading(true);
        
        // Get add-ons configuration for this menu item
        const addOnsConfig = await dataService.getMenuItemAddOns(tenantData.id, menuItem.id!);
        
        if (addOnsConfig && addOnsConfig.addOnCategories) {
          // Load all add-on categories and options
          const [categories, options] = await Promise.all([
            dataService.getAddOnCategories(tenantData.id),
            dataService.getAllAddOnOptions(tenantData.id)
          ]);
          
          setAddOnCategories(categories);
          setAddOnOptions(options);
          
          // Initialize selected add-ons map
          const initialSelection = new Map<string, Set<string>>();
          addOnsConfig.addOnCategories.forEach(cat => {
            initialSelection.set(cat.categoryId, new Set());
          });
          setSelectedAddOns(initialSelection);
        }
      } catch (error) {
        console.error('Error loading add-ons:', error);
        setError('Failed to load add-ons');
      } finally {
        setLoading(false);
      }
    };

    loadAddOns();
  }, [tenantData, menuItem]);

  const handleOptionToggle = (categoryId: string, optionId: string) => {
    setSelectedAddOns(prev => {
      const newSelection = new Map(prev);
      const categorySelection = new Set(newSelection.get(categoryId) || []);
      
      if (categorySelection.has(optionId)) {
        categorySelection.delete(optionId);
      } else {
        categorySelection.add(optionId);
      }
      
      newSelection.set(categoryId, categorySelection);
      return newSelection;
    });
  };

  const handleConfirm = () => {
    const selectedAddOnsArray = Array.from(selectedAddOns.entries()).map(([categoryId, selectedOptions]) => {
      const category = addOnCategories.find(cat => cat.id === categoryId);
      const options = Array.from(selectedOptions).map(optionId => {
        const option = addOnOptions.find(opt => opt.id === optionId);
        return {
          optionId: optionId,
          optionName: option?.name || '',
          price: option?.price || 0
        };
      });
      
      return {
        categoryId,
        categoryName: category?.name || '',
        selectedOptions: options
      };
    });

    onConfirm(selectedAddOnsArray);
  };

  const getCategoryOptions = (categoryId: string) => {
    return addOnOptions.filter(option => option.categoryId === categoryId);
  };

  const getSelectedOptionsCount = (categoryId: string) => {
    return selectedAddOns.get(categoryId)?.size || 0;
  };

  const isCategoryValid = (category: AddOnCategory) => {
    const selectedCount = getSelectedOptionsCount(category.id!);
    return selectedCount >= category.minSelections && 
           (category.maxSelections === 0 || selectedCount <= category.maxSelections);
  };

  const getTotalAddOnsPrice = () => {
    let total = 0;
    selectedAddOns.forEach((selectedOptions, categoryId) => {
      selectedOptions.forEach(optionId => {
        const option = addOnOptions.find(opt => opt.id === optionId);
        if (option) {
          total += option.price;
        }
      });
    });
    return total;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-700 dark:text-gray-300">Loading add-ons...</span>
          </div>
        </div>
      </div>
    );
  }

  if (addOnCategories.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No add-ons available for this item
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Customize {menuItem.name}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select your add-ons and preferences
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
          </div>
        )}

        {/* Add-ons Selection */}
        <div className="p-6 space-y-6">
          {addOnCategories.map(category => {
            const categoryOptions = getCategoryOptions(category.id!);
            const selectedCount = getSelectedOptionsCount(category.id!);
            const isValid = isCategoryValid(category);
            
            return (
              <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                {/* Category Header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      {category.name}
                    </h3>
                    {category.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {category.description}
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {category.minSelections > 0 && (
                      <span className="mr-2">Min: {category.minSelections}</span>
                    )}
                    {category.maxSelections > 0 && (
                      <span>Max: {category.maxSelections}</span>
                    )}
                    {category.maxSelections === 0 && (
                      <span>Unlimited</span>
                    )}
                  </div>
                </div>

                {/* Selection Status */}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Selected: {selectedCount}
                    </span>
                    {!isValid && (
                      <span className="text-red-600 dark:text-red-400">
                        {selectedCount < category.minSelections 
                          ? `Please select at least ${category.minSelections} option(s)`
                          : `Please select no more than ${category.maxSelections} option(s)`
                        }
                      </span>
                    )}
                  </div>
                </div>

                {/* Options Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categoryOptions.map(option => {
                    const isSelected = selectedAddOns.get(category.id!)?.has(option.id!) || false;
                    
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleOptionToggle(category.id!, option.id!)}
                        disabled={!isSelected && category.maxSelections > 0 && selectedCount >= category.maxSelections}
                        className={`p-3 border rounded-lg text-left transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        } ${
                          !isSelected && category.maxSelections > 0 && selectedCount >= category.maxSelections
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              {isSelected && (
                                <Check className="w-4 h-4 text-blue-600" />
                              )}
                              <span className={`font-medium ${
                                isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'
                              }`}>
                                {option.name}
                              </span>
                            </div>
                            {option.description && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {option.description}
                              </p>
                            )}
                          </div>
                          {option.price > 0 && (
                            <span className={`text-sm font-medium ${
                              isSelected ? 'text-blue-600' : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              +₹{option.price}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <span>Base price: ₹{menuItem.price}</span>
              {getTotalAddOnsPrice() > 0 && (
                <span className="ml-2">+ Add-ons: ₹{getTotalAddOnsPrice()}</span>
              )}
            </div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              Total: ₹{menuItem.price + getTotalAddOnsPrice()}
            </div>
          </div>
          
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Add to Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOnsSelectionModal;
